class SwiperSlide extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.thumbsSwiper = null;
  }

  connectedCallback() {
    const swiperSelector = this.getAttribute("main-swiper");
    const swiperEl = swiperSelector ? this.querySelector(swiperSelector) : null;

    if (!swiperEl || typeof Swiper === "undefined") return;

    const thumbsSelector = this.getAttribute("thumbs");
    const thumbsEl = thumbsSelector ? this.querySelector(thumbsSelector) : null;

    if (thumbsEl) {
      const thumbsOptions = {
        direction: this.getAttribute("thumbs-direction") || "horizontal",
        slidesPerView: this.numberAttr("thumbs-slides-per-view", 4),
        spaceBetween: this.numberAttr("thumbs-space-between", 10),
        watchSlidesProgress: this.hasAttribute("thumbs-watch-progress"),
        slideToClickedSlide: this.hasAttribute("thumbs-click-to-slide"),
      };

      const thumbsBreakpoints = this.getAttribute("thumbs-breakpoints");

      if (thumbsBreakpoints) {
        try {
          thumbsOptions.breakpoints = JSON.parse(thumbsBreakpoints);
        } catch (e) {
          console.warn("Invalid thumbs-breakpoints JSON", e);
        }
      }

      this.thumbsSwiper = new Swiper(thumbsEl, thumbsOptions);
    }

    const options = {
      slidesPerView: this.numberAttr("slides-per-view", 1),
      spaceBetween: this.numberAttr("space-between", 0),
      centeredSlides: this.hasAttribute("centered"),
      loop: this.hasAttribute("loop"),
      initialSlide: this.numberAttr("initial-slide", 0),
      slideToClickedSlide: this.hasAttribute("clicked-to-slide"),
      speed: this.numberAttr("speed", 300),
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    };

    const nextSel = this.getAttribute("next");
    const prevSel = this.getAttribute("prev");

    if (nextSel && prevSel) {
      options.navigation = {
        nextEl: this.querySelector(nextSel),
        prevEl: this.querySelector(prevSel),
      };
    }

    const paginationSel = this.getAttribute("pagination");
    const paginationType = this.getAttribute("pagination-type");

    if (paginationSel) {
      const paginationEl = this.querySelector(paginationSel);

      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
        };

        if (paginationType) {
          options.pagination.type = paginationType;
        }

        /**
         * Optional labelled pagination.
         * Only activates when the custom element has:
         * pagination-labels
         *
         * Example:
         * <swiper-slide pagination-labels ...>
         */
        if (this.hasAttribute("pagination-labels")) {
          const slides = swiperEl.querySelectorAll(
            ".swiper-wrapper > .swiper-slide",
          );

          const labels = Array.from(slides).map((slide, index) => {
            return (
              slide.getAttribute("data-pagination-label") ||
              slide.dataset.paginationLabel ||
              `Slide ${index + 1}`
            );
          });

          options.pagination.renderBullet = (index, className) => {
            const label = this.escapeHtml(
              labels[index] || `Slide ${index + 1}`,
            );

            return `
              <button
                class="${className}"
                type="button"
                aria-label="Go to ${label}"
              >
                ${label}
              </button>
            `;
          };
        }
      }
    }

    const breakpoints = this.getAttribute("breakpoints");

    if (breakpoints) {
      try {
        options.breakpoints = JSON.parse(breakpoints);
      } catch (e) {
        console.warn("Invalid breakpoints JSON", e);
      }
    }

    if (this.thumbsSwiper) {
      options.thumbs = { swiper: this.thumbsSwiper };
    }

    this.mainSwiper = new Swiper(swiperEl, options);
  }

  disconnectedCallback() {
    if (this.mainSwiper) this.mainSwiper.destroy(true, true);
    if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
  }

  numberAttr(name, fallback) {
    const val = this.getAttribute(name);
    return val !== null ? Number(val) : fallback;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}

customElements.define("swiper-slide", SwiperSlide);
